import { formatFileSize } from "./formatFileSize";
import {
  WATERMARK_CONTENT_TYPES,
  getWatermarkFontStack,
  measureWatermarkText,
  watermarkFontSizePx,
  watermarkLogoDisplaySize,
  watermarkTilePositions,
} from "../features/tools/watermark/Popup/watermarkSettings";

const urlBytesCache = new Map();

/**
 * Byte length of an image/blob/data URL (cached per URL string).
 */
export async function measureImageUrlBytes(url) {
  if (!url || typeof url !== "string") return null;
  if (urlBytesCache.has(url)) return urlBytesCache.get(url);

  try {
    let bytes = null;
    if (url.startsWith("data:")) {
      const comma = url.indexOf(",");
      const payload = comma >= 0 ? url.slice(comma + 1) : "";
      const isBase64 = /;base64/i.test(url.slice(0, Math.max(0, comma)));
      bytes = isBase64
        ? Math.floor((payload.length * 3) / 4)
        : new TextEncoder().encode(decodeURIComponent(payload)).length;
    } else {
      const res = await fetch(url);
      const blob = await res.blob();
      bytes = blob.size;
    }
    if (Number.isFinite(bytes) && bytes >= 0) {
      urlBytesCache.set(url, bytes);
      return bytes;
    }
  } catch {
    // ignore
  }
  return null;
}

export function formatSizeLabel(bytes, { approx = false } = {}) {
  if (!Number.isFinite(bytes) || bytes < 0) return null;
  const label = formatFileSize(bytes);
  return approx ? `~${label}` : label;
}

function loadHtmlImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (
      typeof url === "string" &&
      /^https?:\/\//i.test(url) &&
      !url.startsWith("blob:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = url;
  });
}

async function drawWatermarkMarks(ctx, watermark, stageW, stageH) {
  if (!watermark || !stageW || !stageH) return;

  const isLogoMode =
    watermark.contentType === WATERMARK_CONTENT_TYPES.LOGO ||
    watermark.contentType === WATERMARK_CONTENT_TYPES.SIGNATURE;
  const logoUrl = watermark.logoUrl || null;
  const rotation = Number(watermark.rotation) || 0;
  const offsetX = Number(watermark.offsetX) || 0;
  const offsetY = Number(watermark.offsetY) || 0;
  const opacity =
    Math.max(0, Math.min(100, Number(watermark.opacity) || 0)) / 100;
  const fontFamily = getWatermarkFontStack(watermark.fontId || watermark.fontFamily);
  const fontSizePx = watermarkFontSizePx(watermark.size, stageW, stageH);

  let markW = 0;
  let markH = 0;
  let logoImg = null;

  if (isLogoMode && logoUrl) {
    const size = watermarkLogoDisplaySize(
      watermark.size,
      stageW,
      stageH,
      watermark.logoNaturalWidth,
      watermark.logoNaturalHeight,
      watermark.logoCropRect
    );
    markW = size.width;
    markH = size.height;
    try {
      logoImg = await loadHtmlImage(logoUrl);
    } catch {
      return;
    }
  } else if (!isLogoMode) {
    const text = watermark.text?.trim() ? watermark.text : "Your Text";
    const metrics = measureWatermarkText(
      text,
      fontSizePx,
      fontFamily,
      watermark.fontWeight,
      {
        letterSpacing: watermark.letterSpacing,
        textScript: watermark.textScript,
      }
    );
    markW = metrics.width;
    markH = metrics.height;
  } else {
    return;
  }

  if (!(markW > 0) || !(markH > 0)) return;

  const marks = watermarkTilePositions({
    tile: watermark.tile || "single",
    spacing: watermark.spacing,
    stageWidth: stageW,
    stageHeight: stageH,
    textWidth: markW,
    textHeight: markH,
    fontSizePx: isLogoMode ? Math.max(markW, markH) : fontSizePx,
    rotationDeg: rotation,
    position: watermark.position || "center",
    margin: watermark.margin,
    offsetX,
    offsetY,
    flushEdges: watermark.flushEdges !== false,
  });

  ctx.save();
  ctx.globalAlpha = opacity;
  for (const pos of marks) {
    const cx = pos.x * stageW;
    const cy = pos.y * stageH;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((rotation * Math.PI) / 180);
    if (logoImg) {
      ctx.drawImage(logoImg, -markW / 2, -markH / 2, markW, markH);
    } else {
      const text = watermark.text?.trim() ? watermark.text : "Your Text";
      ctx.fillStyle = watermark.color || "#FFFFFF";
      ctx.font = `${watermark.fontStyle === "italic" ? "italic " : ""}${
        watermark.fontWeight === "bold" ? "700" : "400"
      } ${fontSizePx}px ${fontFamily}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, 0, 0);
    }
    ctx.restore();
  }
  ctx.restore();
}

/**
 * Estimate export-ish PNG size from preview image (+ optional live watermark).
 */
export async function measurePreviewRasterBytes(
  imageUrl,
  { maxEdge = 960, watermark = null } = {}
) {
  if (!imageUrl) return null;
  try {
    const img = await loadHtmlImage(imageUrl);
    const nw = img.naturalWidth || img.width || 0;
    const nh = img.naturalHeight || img.height || 0;
    if (!nw || !nh) return measureImageUrlBytes(imageUrl);

    const scale = Math.min(1, maxEdge / Math.max(nw, nh));
    const w = Math.max(1, Math.round(nw * scale));
    const h = Math.max(1, Math.round(nh * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return measureImageUrlBytes(imageUrl);
    ctx.drawImage(img, 0, 0, w, h);
    if (watermark) {
      await drawWatermarkMarks(ctx, watermark, w, h);
    }

    const blob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/png");
    });
    return blob?.size ?? null;
  } catch {
    return measureImageUrlBytes(imageUrl);
  }
}
